'use client';

import { useState } from 'react';
import { useGameStore } from '@/store/useGameStore';
import { BadgeGrid } from '@/components/gamification/BadgeGrid';
import { XPBar } from '@/components/gamification/XPBar';
import { StreakDisplay } from '@/components/gamification/StreakDisplay';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { CategoryIcon } from '@/components/ui/CategoryIcon';
import badgesData from '@/data/badges.json';
import categoriesData from '@/data/categories.json';
import type { Badge, Category } from '@/types';
import { Trophy, Zap, Target, AlertTriangle } from 'lucide-react';

const badges = badgesData as Badge[];
const categories = categoriesData as Category[];

export default function ProfilePage() {
  const {
    xp,
    level,
    levelName,
    streak,
    longestStreak,
    answeredQuestions,
    unlockedBadges,
    quizHistory,
    categoryProgress,
    totalQuestionsRead,
    resetProgress,
  } = useGameStore();

  const [showConfirm, setShowConfirm] = useState(false);

  const totalAnswered = answeredQuestions.length;
  const totalQuestions = categories.reduce((s, c) => s + c.questionCount, 0);
  const overallPct = totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
      <h1
        className="font-display font-bold mb-8"
        style={{ fontSize: 'clamp(1.75rem, 4vw, 2rem)', color: 'var(--color-text-primary)' }}
      >
        My <span className="gradient-text">Profile</span>
      </h1>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total XP', value: xp.toLocaleString(), icon: Zap, color: 'var(--color-primary)' },
          { label: 'Current Level', value: `${level} · ${levelName}`, icon: Trophy, color: 'var(--color-secondary)' },
          { label: 'Day Streak', value: streak, icon: null, emoji: '🔥', color: 'var(--color-accent)' },
          { label: 'Questions Done', value: `${totalAnswered}/${totalQuestions}`, icon: Target, color: 'var(--color-primary)' },
        ].map(({ label, value, icon: Icon, emoji, color }) => (
          <div key={label} className="card-base p-4 text-center">
            <div className="mb-2">
              {emoji ? (
                <span className="text-2xl">{emoji}</span>
              ) : Icon ? (
                <Icon size={20} style={{ color, margin: '0 auto' }} />
              ) : null}
            </div>
            <div className="font-display font-bold text-lg" style={{ color }}>{value}</div>
            <div className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* XP Bar */}
      <div className="card-base p-5 mb-8">
        <h2 className="font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
          <Zap size={16} style={{ color: 'var(--color-primary)' }} />
          Level Progress
        </h2>
        <XPBar />
      </div>

      {/* Category Progress */}
      <div className="card-base p-5 mb-8" id="achievements">
        <h2 className="font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
          Category Progress
        </h2>
        <div className="space-y-3">
          {categories.sort((a, b) => a.order - b.order).map((cat) => {
            const answered = categoryProgress[cat.slug] ?? 0;
            const pct = cat.questionCount > 0 ? Math.round((answered / cat.questionCount) * 100) : 0;
            return (
              <div key={cat.slug}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    <CategoryIcon name={cat.icon} size={13} style={{ color: cat.color, display: 'inline-block', marginRight: 4, verticalAlign: 'middle' }} />
                    {cat.name}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    {answered}/{cat.questionCount}
                  </span>
                </div>
                <ProgressBar
                  value={pct}
                  color={cat.color}
                  height={6}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Badges */}
      <div className="mb-8">
        <h2 className="font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
          Badges ({unlockedBadges.length}/{badges.length} unlocked)
        </h2>
        <BadgeGrid badges={badges} />
      </div>

      {/* Quiz History */}
      {quizHistory.length > 0 && (
        <div className="card-base overflow-hidden mb-8" id="history">
          <div className="px-5 py-3" style={{ borderBottom: '1px solid var(--color-border)' }}>
            <h2 className="font-semibold text-sm" style={{ color: 'var(--color-text-primary)' }}>
              Recent Quiz History
            </h2>
          </div>
          <div>
            {quizHistory.slice(-10).reverse().map((result) => {
              const pct = Math.round((result.score / result.total) * 100);
              return (
                <div
                  key={result.id}
                  className="flex items-center justify-between px-5 py-3"
                  style={{ borderBottom: '1px solid var(--color-border)' }}
                >
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                      {result.category === 'mixed' ? '🎲 Mixed' : result.category}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                      {new Date(result.date).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p
                      className="text-sm font-bold"
                      style={{ color: pct >= 80 ? 'var(--color-secondary)' : pct >= 60 ? 'var(--color-primary)' : 'var(--color-accent)' }}
                    >
                      {result.score}/{result.total} · {pct}%
                    </p>
                    <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                      +{result.xpEarned} XP
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Danger zone */}
      <div className="card-base p-5 border border-red-500/20">
        <h2 className="font-semibold mb-2 flex items-center gap-2 text-red-400">
          <AlertTriangle size={16} />
          Danger Zone
        </h2>
        <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
          Reset all progress, XP, badges, and quiz history. This cannot be undone.
        </p>
        {showConfirm ? (
          <div className="flex gap-3">
            <button
              onClick={() => { resetProgress(); setShowConfirm(false); }}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-500 text-white"
            >
              Yes, Reset Everything
            </button>
            <button
              onClick={() => setShowConfirm(false)}
              className="btn-ghost text-sm px-4 py-2"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowConfirm(true)}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-red-400 border border-red-500/30"
          >
            Reset All Progress
          </button>
        )}
      </div>
    </div>
  );
}
